import { useQuery } from "@tanstack/react-query";
import { internalUsersApi } from "@/features/tasks/api/users";

export interface UseInternalUsersOptions {
  /**
   * If true, the response also includes Contractor accounts that have
   * has_friday_access=True. Use this in every Friday (tasks) context where
   * external contractors invited to Friday should be assignable / mentionable
   * / shown in the Osoby picker.
   */
  includeFridayContractors?: boolean;
  /**
   * If true, the response includes ALL active Contractor accounts (no Friday
   * flag required) — used by the meetings invitee picker.
   */
  includeContractors?: boolean;
  /**
   * If true, internal users are limited to the caller's own branch plus those
   * flagged is_cross_branch_visible. Use in pickers that pick *who does the
   * work* (task / subtask assignment); leave off for mentions, filters and
   * anything that must still resolve users across branches. Friday contractors
   * have no branch and are never scoped out.
   */
  sameBranch?: boolean;
  /**
   * If true, the response also includes Contractor accounts granted DLC_02 by the
   * CEO — correspondence register addressee pickers (rejestracja wpływu, dekretacja).
   */
  includeDlc02Contractors?: boolean;
  /**
   * Set to false to skip the request entirely — the backend rejects (403) plain
   * Contractor accounts (HasFridayAccess), so callers reachable by contractors
   * should gate on role instead of firing a request doomed to fail.
   */
  enabled?: boolean;
}

export function useInternalUsers(options: UseInternalUsersOptions = {}) {
  const includeFridayContractors = options.includeFridayContractors ?? false;
  const includeContractors = options.includeContractors ?? false;
  const sameBranch = options.sameBranch ?? false;
  const includeDlc02Contractors = options.includeDlc02Contractors ?? false;
  return useQuery({
    queryKey: ["internal-users", { includeFridayContractors, includeContractors, sameBranch, includeDlc02Contractors }],
    queryFn: () =>
      internalUsersApi.list({ includeFridayContractors, includeContractors, sameBranch, includeDlc02Contractors }),
    staleTime: 5 * 60 * 1000, // 5 min — user list rarely changes
    enabled: options.enabled ?? true,
  });
}
